/**
 * The Roger App username a new account will get.
 *
 * Mirrors `build_clinician_username` in `app/services/rogerdoc_service.py`, which
 * is what actually names the account when the request omits a username. This
 * copy exists so the dialog can pre-fill the field with the real value instead of
 * the placeholder it showed before -- an operator who sees text is far likelier
 * to correct it than one who sees grey "auto-generated from name if blank".
 *
 * Uniqueness is deliberately not checked here, and is not checked on the backend
 * either: two people with the same initial and surname at one agency collide, and
 * Roger App rejects the second. The dialog warns about that separately by looking
 * for an existing account, which is a question only the server can answer.
 */

const NON_ALPHANUMERIC = /[^A-Za-z0-9]/g;

export function buildRogerAppUsername({
  firstName,
  lastName,
  agencyAbbreviation,
}: {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  agencyAbbreviation: string | null | undefined;
}): string | null {
  const initial = (firstName ?? '')
    .replace(NON_ALPHANUMERIC, '')
    .slice(0, 1)
    .toLowerCase();
  const surname = (lastName ?? '').replace(NON_ALPHANUMERIC, '').toLowerCase();
  // The backend takes the first three surviving characters of the agency code.
  const agencyCode = (agencyAbbreviation ?? '')
    .replace(NON_ALPHANUMERIC, '')
    .slice(0, 3)
    .toLowerCase();

  // Any missing part means no preview rather than a malformed one; the backend
  // makes the same call and leaves the username for Roger App to derive.
  if (!initial || !surname || !agencyCode) return null;

  return `${initial}${surname}${agencyCode}`;
}

/** What a Roger App username may contain: exactly what the generator produces. */
const VALID_USERNAME = /^[a-z0-9]+$/;

/**
 * Why this username cannot be used, or null.
 *
 * The field is editable, so whatever the generator produced can be replaced with
 * anything -- a space, an `@`, a pasted email address. Roger App is the only
 * thing that would reject it, and by then the person already exists in RogerEMR
 * with no app account, which is the half-configured state this whole flow is
 * meant to stop producing.
 *
 * Empty is not an error: the backend derives the username itself when the
 * request omits one.
 */
export function rogerAppUsernameProblem(username: string): string | null {
  const value = username.trim();
  if (!value) return null;
  if (value !== username) {
    return 'Usernames cannot start or end with a space.';
  }
  if (!VALID_USERNAME.test(value)) {
    return 'Usernames can only use lowercase letters and numbers, with no spaces or symbols.';
  }
  return null;
}
